import React, { useCallback } from 'react';
import { Box, Typography, Paper } from '@material-ui/core';
import { Pipeline, Step } from '../types';
import { useTimelineViewStyles } from '../styles/timeline-view.styles';
import { getFullDateTimeLabel } from '../utils/timeline-processing';
import { 
  createPipelineLayoutConfig, 
  calculateStepPosition, 
  calculatePipelineColumnHeight,
  calculateStepDuration,
  MIN_STEP_WIDTH_PERCENTAGE
} from '../utils/positioning';
import { statusIcons as statusIconsMap, statusColors as statusColorsMap } from '../utils/constants';
import { StepTooltip } from './TimelineComponents';
import { AppRoutes } from '../../common/router-common';
import { useHighlightedTrace } from '../context/HighlightedTraceContext';
import {
  PersonSharp as Person
} from '@material-ui/icons';
import { PRIMITIVE_TRIGGER_TYPES } from '../constants';
import { buildExecutionListUrl } from '../utils/route-helper';

/**
 * Props interface for the PipelineHeader component
 */
interface PipelineHeaderProps {
  /** Pipeline object containing metadata and configuration */
  pipeline: Pipeline;
  /** Layout configuration object for positioning and sizing */
  layoutConfig: ReturnType<typeof createPipelineLayoutConfig>;
}
/**
 * PipelineHeader Component
 * 
 * Renders the header information for a single pipeline including name, date,
 * and optional time range information. Displays pipeline metadata in a
 * structured format with proper typography hierarchy.
 * 
 * @component
 * @example
 * ```tsx
 * <PipelineHeader 
 *   pipeline={pipelineData} 
 *   layoutConfig={layoutConfiguration}
 * />
 * ```
 * 
 * @param props - The component props
 * @param props.pipeline - Pipeline object with name, date, and timing info
 * @param props.layoutConfig - Configuration for layout positioning
 * 
 * @returns A styled pipeline header with name, date, and time range
 */
export const PipelineHeader = React.memo(({ pipeline, layoutConfig }: PipelineHeaderProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box className={classes.horizontalPipelineHeader}>
      <Typography variant="body2" className={classes.pipelineName}>
        {pipeline.name}
      </Typography>
      <Typography variant="caption" className={classes.pipelineDate}>
        {pipeline.date}
      </Typography>
      {pipeline.startTime && pipeline.endTime && (
        <Box className={classes.pipelineTimeRange}>
          <Typography variant="caption" className={classes.pipelineStartTime}>
            Start: {getFullDateTimeLabel(pipeline.startTime)}
          </Typography>
          <Typography variant="caption" className={classes.pipelineEndTime}>
            End: {getFullDateTimeLabel(pipeline.endTime)}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

PipelineHeader.displayName = 'PipelineHeader';

/**
 * Props interface for the PipelineHeadersRow component
 */
interface PipelineHeadersRowProps {
  /** Array of pipeline objects to render headers for */
  pipelines: Pipeline[];
  /** Layout configuration object for positioning and sizing */
  layoutConfig: ReturnType<typeof createPipelineLayoutConfig>;
}
/**
 * PipelineHeadersRow Component
 * 
 * Renders a horizontal row containing headers for multiple pipelines.
 * Manages the layout and spacing of pipeline headers across the timeline view.
 * 
 * @component
 * @example
 * ```tsx
 * <PipelineHeadersRow 
 *   pipelines={pipelineArray} 
 *   layoutConfig={layoutConfiguration}
 * />
 * ```
 * 
 * @param props - The component props
 * @param props.pipelines - Array of pipeline objects to display
 * @param props.layoutConfig - Configuration for layout and positioning
 * 
 * @returns A row container with multiple pipeline headers
 */
export const PipelineHeadersRow = React.memo(({ pipelines, layoutConfig }: PipelineHeadersRowProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box 
      className={classes.pipelineHeadersRow}
      style={{ minWidth: `${layoutConfig.totalMinWidth}px` }}
    >
      {pipelines.map((pipeline) => (
        <PipelineHeader 
          key={pipeline.id} 
          pipeline={pipeline} 
          layoutConfig={layoutConfig}
        />
      ))}
    </Box>
  );
});

PipelineHeadersRow.displayName = 'PipelineHeadersRow';

/**
 * Props interface for the StepBar component
 */
interface StepBarProps {
  /** Step object containing execution details and status */
  step: Step;
  /** Parent pipeline object for context */
  pipeline: Pipeline;
  /** Layout configuration object for positioning calculations */
  layoutConfig: ReturnType<typeof createPipelineLayoutConfig>;
}
/**
 * StepBar Component
 * 
 * Renders a single step bar within a pipeline timeline. Displays step information
 * including name, duration, status indicator, and running state. Features interactive
 * tooltip and visual status indicators with color coding.
 * 
 * For steps with width less than MIN_STEP_WIDTH_PERCENTAGE, shows only the step
 * without text and icons. On hover, displays the complete step component with
 * default width and all content.
 * 
 * @component
 * @example
 * ```tsx
 * <StepBar 
 *   step={stepData} 
 *   pipeline={pipelineData}
 *   layoutConfig={layoutConfiguration}
 * />
 * ```
 * 
 * @param props - The component props
 * @param props.step - Step object with execution details and timing
 * @param props.pipeline - Parent pipeline for context and positioning
 * @param props.layoutConfig - Layout configuration for positioning
 * 
 * @returns A styled step bar with status indicators and tooltip
 */
export const StepBar = React.memo(({ step, pipeline, layoutConfig }: StepBarProps) => {
  const classes = useTimelineViewStyles();
  const [isHovered, setIsHovered] = React.useState(false);
  const position = calculateStepPosition(step, pipeline, layoutConfig);
  const duration = calculateStepDuration(step.startTime?.getTime() || null, step.endTime?.getTime() || null);

  // Check if step is narrow (less than minimum width percentage)
  const isNarrowStep = position.width < MIN_STEP_WIDTH_PERCENTAGE;

  const { highlightedTraceId, setHighlightedTraceId } = useHighlightedTrace();
  const stepTraceId = step.metadata?.runid;
  const isTraceHighlighted = highlightedTraceId != null && stepTraceId === highlightedTraceId;
  // Steps without a runid are not tied to any trace, so they stay visible intentionally
  const isDimmed = highlightedTraceId != null && stepTraceId != null && stepTraceId !== highlightedTraceId;
    
  const routeToWorkflow = useCallback(() => {
    const workflowIdsStr = step.metadata?.workflowIds ?? step.metadata?.workflowinstanceid;
    if (workflowIdsStr) {
      const workflowIds = workflowIdsStr.split(',').map(id => id.trim());
      if (workflowIds.length > 1) {
        window.open(buildExecutionListUrl(workflowIdsStr), '_blank');
      } else {
        window.open(AppRoutes.executionDetail(workflowIds[0]), '_blank');
      }
    }
  }, [step.metadata]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (stepTraceId) setHighlightedTraceId(stepTraceId);
  }, [stepTraceId, setHighlightedTraceId]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setHighlightedTraceId(null);
  }, [setHighlightedTraceId]);

  // Calculate hover styles for narrow steps
  const getStepStyles = () => {
    const baseStyles: React.CSSProperties = {
      left: `${position.left}%`,
      width: `${position.width}%`,
      minHeight: isNarrowStep ? '30px' : '40px',
      top: `${position.top}px`,
      boxShadow: `0 -4px 0 0 ${step.color}, 0 8px 8px rgba(0,0,0,0.1)`,
      cursor: 'pointer',
      zIndex: 10,
      transform: 'none',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transitions
      opacity: isDimmed ? 0.3 : 1,
    };

    if (isNarrowStep && (isHovered || isTraceHighlighted)) {
      // On hover, show with default width and center it around the original position
      const defaultWidth = MIN_STEP_WIDTH_PERCENTAGE;
      const originalCenter = position.left + (position.width / 2);
      const newLeft = Math.max(0, Math.min(100 - defaultWidth, originalCenter - (defaultWidth / 2)));
      
      return {
        ...baseStyles,
        left: `${newLeft}%`,
        width: `${defaultWidth}%`,
        minHeight: '40px',
        boxShadow: `0 -4px 0 0 ${step.color}, 0 12px 20px rgba(0,0,0,0.2)`,
        zIndex: 30, // Higher z-index when hovered
        transform: 'translateY(-4px)', // Slight lift on hover
        opacity: 1,
      };
    }
    
    // Normal state - return base styles with transitions
    return baseStyles;
  };

  return (
    <StepTooltip step={step}>
      <Paper
        key={step.name}
        elevation={2}
        className={`${classes.paperStepBar} ${classes.paperStepBarHorizontal}`}
        style={getStepStyles()}
        onClick={routeToWorkflow}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* For narrow steps, only show content when hovered */}
        {(!isNarrowStep || isHovered || isTraceHighlighted) && (
          <>
            <Box style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Typography 
                variant="caption" 
                className={classes.paperStepLabel}
              >
                {step.name}
              </Typography>
              {duration && (
                <Typography 
                  variant="caption" 
                  className={classes.stepDuration}
                >
                  {duration}
                </Typography>
              )}
            </Box>
            
            {PRIMITIVE_TRIGGER_TYPES.MANUAL === step.triggerType && 
             (
              <Box className={classes.paperIcon}>
                {React.createElement(Person, { 
                  className: classes.paperStatusIcon 
                })}
              </Box>
            )}

            {/* Status indicator */}
            <Box className={classes.paperIcon}  style={{ color: statusColorsMap[step.status as keyof typeof statusColorsMap]}}>
              {React.createElement(statusIconsMap[step.status as keyof typeof statusIconsMap], { 
                className: classes.paperStatusIcon 
              })}
            </Box>
            
          </>
        )}
        
        {/* Running indicator - always show for running steps */}
        {step.status === 'running' && (
          <Box className={classes.runningIndicator} />
        )}
      </Paper>
    </StepTooltip>
  );
});

StepBar.displayName = 'StepBar';

/**
 * Props interface for the PipelineColumn component
 */
interface PipelineColumnProps {
  /** Pipeline object containing steps and metadata */
  pipeline: Pipeline;
  /** Layout configuration object for positioning and sizing */
  layoutConfig: ReturnType<typeof createPipelineLayoutConfig>;
}
/**
 * PipelineColumn Component
 * 
 * Renders a single pipeline column containing all its steps in chronological order.
 * Manages the vertical layout and positioning of step bars within the pipeline timeline.
 * Automatically sorts steps by start time for proper chronological display.
 * 
 * @component
 * @example
 * ```tsx
 * <PipelineColumn 
 *   pipeline={pipelineData} 
 *   layoutConfig={layoutConfiguration}
 * />
 * ```
 * 
 * @param props - The component props
 * @param props.pipeline - Pipeline object with steps and configuration
 * @param props.layoutConfig - Layout configuration for positioning
 * 
 * @returns A column container with chronologically ordered step bars
 */
export const PipelineColumn = React.memo(({ pipeline, layoutConfig }: PipelineColumnProps) => {
  const classes = useTimelineViewStyles();
  
  // Sort steps chronologically within the pipeline
  const sortedSteps = [...pipeline.steps].sort((a, b) => {
    if (a.startTime && b.startTime) {
      return a.startTime.getTime() - b.startTime.getTime();
    }
    return 0;
  });

  const columnHeight = calculatePipelineColumnHeight(pipeline, layoutConfig);

  return (
    <Box 
      key={pipeline.id}
      className={classes.pipelineColumn}
      style={{ minHeight: `${columnHeight}px` }}
    >
      {sortedSteps.map((step, stepIndex) => (
        <StepBar
          key={`${pipeline.id}-${step.lane}-${stepIndex}`}
          step={step}
          pipeline={pipeline}
          layoutConfig={layoutConfig}
        />
      ))}
    </Box>
  );
});

PipelineColumn.displayName = 'PipelineColumn';

/**
 * Props interface for the PipelineStepsContainer component
 */
interface PipelineStepsContainerProps {
  /** Array of pipeline objects to render */
  pipelines: Pipeline[];
  /** Layout configuration object for positioning and sizing */
  layoutConfig: ReturnType<typeof createPipelineLayoutConfig>;
}
/**
 * PipelineStepsContainer Component
 * 
 * Renders the main container for all pipeline columns with their respective steps.
 * Manages the horizontal layout of multiple pipelines and ensures proper spacing
 * and alignment across the timeline view. Handles responsive width calculations.
 * 
 * @component
 * @example
 * ```tsx
 * <PipelineStepsContainer 
 *   pipelines={pipelineArray} 
 *   layoutConfig={layoutConfiguration}
 * />
 * ```
 * 
 * @param props - The component props
 * @param props.pipelines - Array of pipeline objects to display
 * @param props.layoutConfig - Layout configuration for positioning
 * 
 * @returns A horizontal container with multiple pipeline columns
 */
export const PipelineStepsContainer = React.memo(({ pipelines, layoutConfig }: PipelineStepsContainerProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box 
      className={classes.horizontalStepsContainer}
      style={{ minWidth: `${layoutConfig.totalMinWidth}px` }}
    >
      {pipelines.map((pipeline) => (
        <PipelineColumn
          key={pipeline.id}
          pipeline={pipeline}
          layoutConfig={layoutConfig}
        />
      ))}
    </Box>
  );
});

PipelineStepsContainer.displayName = 'PipelineStepsContainer';
